import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
    Box,
    Button,
    Title,
    Text,
    SimpleGrid,
    Switch,
    Select,
    Badge,
    Alert,
    ActionIcon,
    Tooltip,
    Table,
    Paper,
    Modal,
    Group,
    Tabs,
} from '@mantine/core';
import {
    IconPlayerPlay,
    IconTrash,
    IconRefresh,
    IconAlertTriangle,
    IconCircleCheck,
    IconAlertCircle,
    IconDeviceFloppy,
} from '@tabler/icons-react';
import { entityService } from '../../services/entityServiceSingleton';
import CentralizedSchemaService from '../../services/CentralizedSchemaService';
import { extractErrorMessage } from '../../utils/apiUtils';
import { debugLog } from '../../utils/debugLog';
import { SetPreviewDialog } from '../dialogs';
import { SetFormData } from '../common/SetForm';
import { formatTimeShort } from '../../utils/dateTimeUtils';

interface GeneratedSet {
    id: string;
    displayName: string;
    description: string;
    filterScope: string;
    type: 'manual' | 'enumerated';
    isSystem: boolean;
    manualMembers?: SetFormData['manualMembers'];
    filter?: string;
    createdAt: Date;
    status: 'created' | 'failed' | 'deleted';
    error?: string;
    errorMessage?: string;
    setData?: any;
}

const SetGen: React.FC = () => {
    const [activeTab, setActiveTab] = useState<string | null>('random');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [allowInvalidSets, setAllowInvalidSets] = useState(false);
    const [generatedSets, setGeneratedSets] = useState<GeneratedSet[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [setToDelete, setSetToDelete] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [selectedEntityType, setSelectedEntityType] = useState<string | null>(null);
    const [availableEntityTypes, setAvailableEntityTypes] = useState<Array<{ id: string, simpleName: string, displayName: string }>>([]);
    const [cancelToken, setCancelToken] = useState<AbortController | null>(null);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [previewData, setPreviewData] = useState<SetFormData | null>(null);
    const [generatedSetData, setGeneratedSetData] = useState<SetFormData | null>(null);

    const schemaService = useMemo(() => CentralizedSchemaService.getInstance(), []);

    // Sample data for random generation
    const sampleFilterScopes = ['student', 'employee', 'contractor', 'teacher', 'administrator', 'parent', 'guardian'];
    const sampleDescriptions = [
        'Test set for data generation',
        'Randomly generated set',
        'Development testing set',
        'Sample set for validation',
        'Generated test data set'
    ];
    const sampleManualIds = [
        'STU001', 'STU002', 'STU003', 'EMP001', 'EMP002', 'TCH001', 'TCH002',
        'ADM001', 'CON001', 'CON002', 'PAR001', 'PAR002', 'GUA001'
    ];
    const sampleFilters = [
        'Role eq "Student"',
        'Department eq "Mathematics"',
        'GradeLevel ge 9',
        'Status eq "Active"',
        'Type eq "Employee"',
        'Location eq "Main Campus"'
    ];

    // Load available entity types on component mount
    useEffect(() => {
        const loadEntityTypes = async () => {
            try {
                const schemas = await schemaService.getSchemas();
                const entityTypes = schemas.map((schema: any) => {
                    const schemaId = schema.id || schema.name;
                    if (!schemaId) return null;

                    let simpleName = schemaId;
                    if (schemaId.includes(':')) {
                        const parts = schemaId.split(':');
                        simpleName = parts[parts.length - 1];
                    }

                    return {
                        id: schemaId,
                        simpleName: simpleName,
                        displayName: simpleName.charAt(0).toUpperCase() + simpleName.slice(1)
                    };
                }).filter(Boolean) as Array<{ id: string, simpleName: string, displayName: string }>;

                setAvailableEntityTypes(entityTypes);
            } catch (err) {
                console.error('Error loading entity types:', err);
                const fallbackTypes = sampleFilterScopes.map(type => ({
                    id: type,
                    simpleName: type,
                    displayName: type.charAt(0).toUpperCase() + type.slice(1)
                }));
                setAvailableEntityTypes(fallbackTypes);
            }
        };
        loadEntityTypes();
    }, []);

    // Cleanup: cancel any pending requests when component unmounts
    useEffect(() => {
        return () => {
            if (cancelToken) {
                cancelToken.abort();
            }
        };
    }, [cancelToken]);

    // Generate random set data
    const generateRandomSet = useCallback((): SetFormData => {
        const filterScopeOptions = availableEntityTypes.length > 0 ? availableEntityTypes : sampleFilterScopes.map(type => ({ id: type, simpleName: type, displayName: type }));
        const randomFilterScope = selectedEntityType || filterScopeOptions[Math.floor(Math.random() * filterScopeOptions.length)].simpleName;

        const randomDescription = sampleDescriptions[Math.floor(Math.random() * sampleDescriptions.length)];
        const isManual = Math.random() > 0.5;
        const isSystem = allowInvalidSets && Math.random() > 0.7;

        const set: SetFormData = {
            displayName: `TestSet_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
            description: randomDescription,
            filterScope: randomFilterScope,
            type: isManual ? 'manual' : 'enumerated',
            isSystem: isSystem,
            manualMembers: [],
            filter: ''
        };

        if (isManual) {
            const memberCount = Math.floor(Math.random() * 5) + 1;
            const shuffled = [...sampleManualIds].sort(() => 0.5 - Math.random());
            set.manualMembers = shuffled.slice(0, memberCount);
        } else {
            set.filter = sampleFilters[Math.floor(Math.random() * sampleFilters.length)];
        }

        if (allowInvalidSets) {
            if (Math.random() > 0.8) {
                if (Math.random() > 0.5) {
                    set.displayName = '';
                } else {
                    set.filterScope = '';
                }
            }
        }

        return set;
    }, [allowInvalidSets, selectedEntityType, availableEntityTypes]);

    // Generate and preview a random set
    const handleGeneratePreview = useCallback(() => {
        const randomSet = generateRandomSet();
        setGeneratedSetData(randomSet);
        setPreviewData(randomSet);
        setPreviewDialogOpen(true);
    }, [generateRandomSet]);

    // Create the generated set
    const handleCreateGenerated = useCallback(async () => {
        if (!generatedSetData) return;

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const setEntity: any = {
                displayName: generatedSetData.displayName
            };

            if (generatedSetData.description && generatedSetData.description.trim()) {
                setEntity.description = generatedSetData.description;
            }

            if (generatedSetData.filterScope && generatedSetData.filterScope.trim()) {
                setEntity.filterScope = generatedSetData.filterScope;
            }

            setEntity.type = generatedSetData.type;
            setEntity.isSystem = generatedSetData.isSystem;

            if (generatedSetData.type === 'manual' && generatedSetData.manualMembers.length > 0) {
                setEntity.ManualSetMember = generatedSetData.manualMembers
                    .map((m: any) => typeof m === 'string' ? m : (m?.Id ?? m?.id ?? ''))
                    .map((v: any) => (v ?? '').toString().trim())
                    .filter((v: string) => v.length > 0);
            } else if (generatedSetData.type === 'enumerated' && generatedSetData.filter && generatedSetData.filter.trim()) {
                setEntity.filter = generatedSetData.filter;
            }

            const createdSet = await entityService.createEntity('Set', setEntity);
            setSuccess(`Successfully created set: ${generatedSetData.displayName}`);

            const generatedSet: GeneratedSet = {
                id: createdSet.id || createdSet.Id || `set_${Date.now()}`,
                displayName: generatedSetData.displayName,
                description: generatedSetData.description,
                filterScope: generatedSetData.filterScope,
                type: generatedSetData.type,
                isSystem: generatedSetData.isSystem,
                manualMembers: generatedSetData.manualMembers,
                filter: generatedSetData.filter,
                createdAt: new Date(),
                status: 'created'
            };

            setGeneratedSets(prev => [generatedSet, ...prev]);
            setGeneratedSetData(null);

        } catch (err: any) {
            console.error('Error creating generated set:', err);
            setError(err instanceof Error ? err.message : 'Failed to create set');
        } finally {
            setLoading(false);
        }
    }, [generatedSetData, entityService]);

    // Generate and create a random set
    const handleGenerateRandom = useCallback(async () => {
        if (cancelToken) {
            cancelToken.abort();
        }

        const newCancelToken = new AbortController();
        setCancelToken(newCancelToken);

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const randomSet = generateRandomSet();
            const setId = `set_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

            const setEntity: any = {
                displayName: randomSet.displayName,
                description: randomSet.description,
                filterScope: randomSet.filterScope,
                type: randomSet.type,
                isSystem: randomSet.isSystem
            };

            if (randomSet.type === 'manual' && randomSet.manualMembers) {
                setEntity.ManualSetMember = randomSet.manualMembers
                    .map((m: any) => typeof m === 'string' ? m : (m?.Id ?? m?.id ?? ''))
                    .map((v: any) => (v ?? '').toString().trim())
                    .filter((v: string) => v.length > 0);
            } else if (randomSet.type === 'enumerated' && randomSet.filter) {
                setEntity.filter = randomSet.filter;
            }

            let createdSet;
            let status: 'created' | 'failed' = 'created';
            let error: string | undefined;
            let errorMessage: string | undefined;

            try {
                createdSet = await entityService.createEntity('Set', setEntity);
                setSuccess(`Successfully created set: ${randomSet.displayName}`);
            } catch (createError: any) {
                if (createError.name === 'AbortError' || newCancelToken.signal.aborted) {
                    debugLog('Set creation was cancelled');
                    setCancelToken(null);
                    setLoading(false);
                    return;
                }

                status = 'failed';
                error = createError instanceof Error ? createError.message : 'Unknown error';

                if (createError?.response?.data?.Message) {
                    errorMessage = createError.response.data.Message;
                } else if (createError?.response?.data?.message) {
                    errorMessage = createError.response.data.message;
                } else if (createError?.response?.data?.error) {
                    errorMessage = createError.response.data.error;
                } else if (createError?.response?.data) {
                    errorMessage = typeof createError.response.data === 'string'
                        ? createError.response.data
                        : JSON.stringify(createError.response.data);
                }

                console.error('Failed to create set:', createError);
            }

            const generatedSet: GeneratedSet = {
                id: setId,
                displayName: randomSet.displayName || 'Invalid Set',
                description: randomSet.description || '',
                filterScope: randomSet.filterScope || '',
                type: randomSet.type!,
                isSystem: randomSet.isSystem || false,
                manualMembers: randomSet.manualMembers,
                filter: randomSet.filter,
                createdAt: new Date(),
                status: status,
                error: error,
                errorMessage: errorMessage,
                setData: setEntity
            };

            setGeneratedSets(prev => [generatedSet, ...prev]);

        } catch (err) {
            if (err instanceof Error && (err.name === 'AbortError' || newCancelToken.signal.aborted)) {
                debugLog('Set generation was cancelled');
                setCancelToken(null);
                setLoading(false);
                return;
            }

            console.error('Error generating random set:', err);
            setError(err instanceof Error ? err.message : 'Failed to generate random set');
        } finally {
            setLoading(false);
            setCancelToken(null);
        }
    }, [generateRandomSet, entityService, cancelToken]);

    // Retry creating a failed set
    const handleRetrySet = useCallback(async (setId: string) => {
        const setToRetry = generatedSets.find(set => set.id === setId);
        if (!setToRetry || !setToRetry.setData) {
            setError('Cannot retry: Set data not available');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const createdSet = await entityService.createEntity('Set', setToRetry.setData);
            setSuccess(`Successfully retried and created set: ${setToRetry.displayName}`);

            setGeneratedSets(prev => prev.map(set =>
                set.id === setId
                    ? { ...set, status: 'created' as const, error: undefined, errorMessage: undefined }
                    : set
            ));
        } catch (err: any) {
            console.error('Error retrying set creation:', err);
            const errorMessage = extractErrorMessage(err);

            setGeneratedSets(prev => prev.map(set =>
                set.id === setId
                    ? {
                        ...set,
                        error: err instanceof Error ? err.message : 'Unknown error',
                        errorMessage: errorMessage
                    }
                    : set
            ));

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [generatedSets, entityService]);

    // Delete a specific set
    const handleDeleteSet = useCallback(async (setId: string) => {
        setDeleting(true);
        try {
            await entityService.deleteEntity('Set', setId);
            setGeneratedSets(prev => prev.map(set =>
                set.id === setId ? { ...set, status: 'deleted' as const } : set
            ));
            setSuccess('Set deleted successfully');
        } catch (err) {
            console.error('Error deleting set:', err);
            setError(err instanceof Error ? err.message : 'Failed to delete set');
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
            setSetToDelete(null);
        }
    }, [entityService]);

    // Clear all generated sets from the list
    const handleClearAll = useCallback(() => {
        setGeneratedSets([]);
        setSuccess('Cleared all generated sets from list');
    }, []);

    // Cancel current generation
    const handleCancelGeneration = useCallback(() => {
        if (cancelToken) {
            cancelToken.abort();
            setCancelToken(null);
            setLoading(false);
            setSuccess('Generation cancelled');
        }
    }, [cancelToken]);

    // Handle successful set creation from preview
    const handlePreviewSuccess = useCallback((createdSet: any) => {
        setSuccess(`Successfully created set: ${createdSet.displayName || createdSet.DisplayName}`);

        const generatedSet: GeneratedSet = {
            id: createdSet.id || createdSet.Id || `set_${Date.now()}`,
            displayName: createdSet.displayName || createdSet.DisplayName || 'Created Set',
            description: createdSet.description || createdSet.Description || '',
            filterScope: createdSet.filterScope || createdSet.FilterScope || '',
            type: (createdSet.type || createdSet.Type || 'manual') as 'manual' | 'enumerated',
            isSystem: createdSet.isSystem || createdSet.IsSystem || false,
            manualMembers: createdSet.manualSetMember || createdSet.ManualSetMember || [],
            filter: createdSet.filter || createdSet.Filter || '',
            createdAt: new Date(),
            status: 'created'
        };

        setGeneratedSets(prev => [generatedSet, ...prev]);
    }, []);

    // Refresh the page
    const handleRefresh = useCallback(() => {
        window.location.reload();
    }, []);

    // Get status icon
    const getStatusIcon = (status: GeneratedSet['status']) => {
        switch (status) {
            case 'created':
                return <IconCircleCheck size={18} color="var(--mantine-color-green-6)" />;
            case 'failed':
                return <IconAlertCircle size={18} color="var(--mantine-color-red-6)" />;
            case 'deleted':
                return <IconTrash size={18} color="var(--mantine-color-gray-5)" />;
            default:
                return null;
        }
    };

    const entityTypeSelectData = [
        { value: '', label: 'Random Entity Type (unselected)' },
        ...availableEntityTypes.map((et) => ({
            value: et.simpleName,
            label: et.displayName,
        })),
    ];

    return (
        <Box p="lg">
            <Title order={4} mb="xs">
                Set Generator
            </Title>
            <Text size="sm" c="dimmed" mb="lg">
                Generate sets for testing and development. Use Random tab for random generation.
            </Text>

            <Tabs value={activeTab} onChange={setActiveTab} mb="lg">
                <Tabs.List>
                    <Tabs.Tab value="random">Random</Tabs.Tab>
                </Tabs.List>
            </Tabs>

            {activeTab === 'random' && (
                <>
                    <Paper withBorder p="md" mb="lg">
                        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" verticalSpacing="md">
                            <Box>
                                <Switch
                                    label={
                                        <Group gap="xs">
                                            <IconAlertTriangle size={18} color="var(--mantine-color-yellow-6)" />
                                            Allow Invalid Sets
                                        </Group>
                                    }
                                    checked={allowInvalidSets}
                                    onChange={(e) => setAllowInvalidSets(e.currentTarget.checked)}
                                    color="yellow"
                                />
                                <Text size="xs" c="dimmed" mt="xs" display="block">
                                    {allowInvalidSets
                                        ? 'Will generate sets with missing required fields to test validation'
                                        : 'Will only generate valid sets'
                                    }
                                </Text>
                            </Box>
                            <Box>
                                <Select
                                    label="Entity Type (Filter Scope)"
                                    placeholder="Random Entity Type (unselected)"
                                    data={entityTypeSelectData}
                                    value={selectedEntityType || ''}
                                    onChange={(v) => setSelectedEntityType(v || null)}
                                    size="sm"
                                    mb="xs"
                                />
                                <Text size="xs" c="dimmed" mt="xs" display="block">
                                    {selectedEntityType
                                        ? `Will generate sets with filter scope: ${selectedEntityType}`
                                        : 'Will generate sets with random filter scopes from all available entity types'
                                    }
                                </Text>
                            </Box>
                            <Group justify="flex-end" align="flex-end">
                                {loading ? (
                                    <Button
                                        variant="outline"
                                        color="red"
                                        leftSection={<IconTrash size={18} />}
                                        onClick={handleCancelGeneration}
                                    >
                                        Cancel
                                    </Button>
                                ) : (
                                    <>
                                        <Button
                                            variant="outline"
                                            color="gray"
                                            leftSection={<IconPlayerPlay size={18} />}
                                            onClick={handleGeneratePreview}
                                            disabled={loading}
                                        >
                                            Generate
                                        </Button>
                                        {generatedSetData && (
                                            <Button
                                                leftSection={<IconDeviceFloppy size={18} />}
                                                onClick={handleCreateGenerated}
                                                disabled={loading}
                                            >
                                                Create Set
                                            </Button>
                                        )}
                                    </>
                                )}
                                <Button
                                    variant="outline"
                                    leftSection={<IconRefresh size={18} />}
                                    onClick={handleRefresh}
                                    disabled={loading}
                                >
                                    Refresh
                                </Button>
                            </Group>
                        </SimpleGrid>
                    </Paper>

                    {generatedSetData && (
                        <Paper withBorder p="md" mb="lg">
                            <Title order={6} mb="xs">
                                Generated Set Ready for Creation
                            </Title>
                            <Group justify="space-between" align="flex-start">
                                <Box flex={1}>
                                    <Text size="sm"><strong>Name:</strong> {generatedSetData.displayName}</Text>
                                    <Text size="sm"><strong>Description:</strong> {generatedSetData.description || 'No description'}</Text>
                                    <Text size="sm"><strong>Filter Scope:</strong> {generatedSetData.filterScope}</Text>
                                    <Text size="sm"><strong>Type:</strong> {generatedSetData.type === 'manual' ? 'Manual Set' : 'Dynamic Set'}</Text>
                                    {generatedSetData.type === 'manual' && (
                                        <Text size="sm"><strong>Members:</strong> {generatedSetData.manualMembers.length} member(s)</Text>
                                    )}
                                    {generatedSetData.type === 'enumerated' && (
                                        <Text size="sm"><strong>Filter:</strong> {generatedSetData.filter || 'No filter'}</Text>
                                    )}
                                    <Text size="sm"><strong>System Set:</strong> {generatedSetData.isSystem ? 'Yes' : 'No'}</Text>
                                </Box>
                                <Button
                                    variant="outline"
                                    size="xs"
                                    color="gray"
                                    onClick={() => setGeneratedSetData(null)}
                                >
                                    Clear
                                </Button>
                            </Group>
                        </Paper>
                    )}

                    {error && (
                        <Alert color="red" mb="md" withCloseButton onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert color="green" mb="md" withCloseButton onClose={() => setSuccess(null)}>
                            {success}
                        </Alert>
                    )}

                    <Paper withBorder p="md">
                        <Group justify="space-between" align="center" mb="md">
                            <Title order={6}>
                                Generated Sets ({generatedSets.length})
                            </Title>
                            {generatedSets.length > 0 && (
                                <Button
                                    variant="outline"
                                    color="yellow"
                                    leftSection={<IconTrash size={18} />}
                                    onClick={handleClearAll}
                                >
                                    Clear All
                                </Button>
                            )}
                        </Group>

                        {generatedSets.length === 0 ? (
                            <Text size="sm" c="dimmed" ta="center" py="xl">
                                No sets generated yet. Click "Generate Random Set" to create test data.
                            </Text>
                        ) : (
                            <Table>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Status</Table.Th>
                                        <Table.Th>Display Name</Table.Th>
                                        <Table.Th>Description</Table.Th>
                                        <Table.Th>Filter Scope</Table.Th>
                                        <Table.Th>Type</Table.Th>
                                        <Table.Th>System</Table.Th>
                                        <Table.Th>Error Message</Table.Th>
                                        <Table.Th>Created</Table.Th>
                                        <Table.Th>Actions</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {generatedSets.map((set) => (
                                        <Table.Tr key={set.id}>
                                            <Table.Td>{getStatusIcon(set.status)}</Table.Td>
                                            <Table.Td>
                                                <Text size="sm" fw={500}>
                                                    {set.displayName}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Text size="sm" c="dimmed">
                                                    {set.description}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge variant="outline" color={set.filterScope ? 'blue' : 'gray'} size="sm">
                                                    {set.filterScope || 'None'}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge variant="outline" color={set.type === 'manual' ? 'gray' : 'blue'} size="sm">
                                                    {set.type}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                {set.isSystem ? (
                                                    <Badge color="yellow" size="sm">Yes</Badge>
                                                ) : (
                                                    <Badge color="gray" size="sm">No</Badge>
                                                )}
                                            </Table.Td>
                                            <Table.Td>
                                                {set.status === 'failed' && (set.errorMessage || set.error) ? (
                                                    <Tooltip label={set.errorMessage || set.error}>
                                                        <Text
                                                            size="xs"
                                                            c="red"
                                                            style={{
                                                                maxWidth: 200,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                display: 'block'
                                                            }}
                                                        >
                                                            {set.errorMessage || set.error}
                                                        </Text>
                                                    </Tooltip>
                                                ) : (
                                                    <Text size="xs" c="dimmed">—</Text>
                                                )}
                                            </Table.Td>
                                            <Table.Td>
                                                <Text size="xs">
                                                    {formatTimeShort(set.createdAt.toISOString())}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                {set.status === 'created' && (
                                                    <Tooltip label="Delete Set">
                                                        <ActionIcon
                                                            size="sm"
                                                            color="red"
                                                            variant="subtle"
                                                            onClick={() => {
                                                                setSetToDelete(set.id);
                                                                setDeleteDialogOpen(true);
                                                            }}
                                                        >
                                                            <IconTrash size={16} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}
                                                {set.status === 'failed' && (
                                                    <Group gap="xs">
                                                        <Tooltip label={set.errorMessage || set.error || 'Creation failed'}>
                                                            <ActionIcon size="sm" color="red" variant="subtle">
                                                                <IconAlertCircle size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                        <Tooltip label="Retry Creation">
                                                            <ActionIcon
                                                                size="sm"
                                                                onClick={() => handleRetrySet(set.id)}
                                                                disabled={loading}
                                                            >
                                                                <IconRefresh size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    </Group>
                                                )}
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        )}
                    </Paper>
                </>
            )}

            <Modal
                opened={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                title="Delete Set"
                size="sm"
            >
                <Text size="sm" mb="md">
                    Are you sure you want to delete this set? This action cannot be undone.
                </Text>
                <Group justify="flex-end">
                    <Button variant="subtle" onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        color="red"
                        onClick={() => setToDelete && handleDeleteSet(setToDelete)}
                        loading={deleting}
                        leftSection={<IconTrash size={18} />}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </Group>
            </Modal>

            {/* Preview Dialog */}
            <SetPreviewDialog
                open={previewDialogOpen}
                onClose={() => setPreviewDialogOpen(false)}
                onSuccess={handlePreviewSuccess}
                initialData={previewData || undefined}
                mode="preview"
                title="Preview Generated Set"
            />
        </Box>
    );
};

export default SetGen;

